import dayjs from "dayjs";
import { Calendar, Popover } from "frost-ui-react";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import AppText from "../../../components/Core/AppText/AppText";
import Flex from "../../../components/Layout/Flex";
import Breadcrumbs, { BreadcrumbItem } from "../../../components/Shared/Breadcrumbs/Breadcrumbs";
import { DetailHeaderTags } from "../../../components/Shared/DetailHeader";
import InfoBlock, { InfoBlocStyles } from "../../../components/Shared/InfoBlock/InfoBlock";
import JobStatusTag from "../../../components/Shared/JobStatusTag";
import Tag from "../../../components/Shared/Tag/Tag";
import { JobBreadcrumbs } from "../../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import { JOB_STATUS } from "../../../configs/constants/JobStatus";
import { USER_TYPE } from "../../../configs/constants/UserTypes";
import { CompetenceItem } from "../../../models/Competence";
import Job, { Application, JobShift } from "../../../models/Job";
import User from "../../../models/User";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { appTheme } from "../../../theme/Theme";
import { displayCompetences } from "../../../utils/competence.utils";
import { denialReasonLabel } from "../../../utils/job.utils";
import {
	getBrowserLocale,
	getFromToWeekdayDate,
	shiftTimeFromTo,
} from "../../../utils/date.time.utils";
import { jobRoute } from "../../../utils/route.utils";
import {
	addDotsIfLong,
	decimalToPercentage,
	formatCurrencyString,
} from "../../../utils/string.utils";

interface Props {
	job?: Job;
	className?: string;
	/**
	 * All competences for customer, used to display competence string
	 */
	competences: CompetenceItem[];

	/**
	 * The booked application, if any
	 */
	bookedApplication?: Application;

	/**
	 * Toggle a DESCRIPTIVE job tag (org `jobTagTypes`) on/off. When provided (and
	 * the org defines job tag types) the tags row gets an inline "+" picker.
	 */
	onToggleTag?: (key: string) => void;
}

const JobHeader: React.FC<Props> = ({
	job,
	className,
	competences,
	bookedApplication,
	onToggleTag,
}) => {
	const { t } = useTranslation();

	const { data: organisation } = useOrganisation();

	// Resolve the job's `{ key }` tags against the org taxonomy (falling back to
	// the raw key for keys deleted from the taxonomy), preserving the job's order.
	const jobTagTypes = organisation?.jobTagTypes ?? [];
	const jobTags = (job?.tags ?? []).map((held) => {
		const type = jobTagTypes.find((tagType) => tagType.key === held.key);
		return { key: held.key, name: type?.name.sv ?? held.key, color: type?.color ?? undefined };
	});
	const jobTagOptions = jobTagTypes.map((tagType) => ({
		key: tagType.key,
		name: tagType.name.sv,
		color: tagType.color ?? undefined,
	}));

	const [timePopover, setTimePopover] = useState<{
		left: number;
		top: number;
		shifts: JobShift[];
	}>();

	const OrderDetailsBreadcrumbs: BreadcrumbItem[] = [
		...JobBreadcrumbs,
		{
			title: job?.customerOrderNumber
				? `${job?.orderNumber} (${addDotsIfLong(job.customerOrderNumber, 20)})`
				: job?.orderNumber || "",
			path: jobRoute(job?.id || ""),
		},
	];

	const shiftHighlights = (job?.shifts || []).map((shift) => ({
		date: new Date(shift.start),
		type: "value",
	}));

	const address = job?.address
		? `${job.address?.address1 ? job.address?.address1 + ", " : ""} ${job.address?.city ?? ""}`
		: "";

	const tgTb = bookedApplication
		? `${formatCurrencyString(bookedApplication.tb, {
				hideCurrency: true,
		  })} SEK/h, ${decimalToPercentage(bookedApplication.tg)}`
		: "-";

	const contactPerson = job?.contactPersons[0];

	return (
		<StyledJobHeader className={className}>
			<div className="inner">
				<Breadcrumbs items={OrderDetailsBreadcrumbs} className="border-none p-0 pl-2 h-[25px]" />
				<div className="date-picker-ctn" onMouseOut={() => setTimePopover(undefined)}>
					<Calendar
						locale={getBrowserLocale()}
						weeks={2}
						monthFormat="MMM"
						highlights={shiftHighlights}
						focusOnDate={new Date(job?.startDate || new Date())}
						onHover={(e, date) => {
							const { offsetLeft, offsetTop } = e.currentTarget;

							const dayShifts = (job?.shifts || []).filter((shift) => {
								return dayjs(shift.start).isSame(date, "day");
							});

							if (dayShifts.length) {
								setTimePopover({
									left: offsetLeft - 5,
									top: offsetTop + 40,
									shifts: dayShifts,
								});
							} else {
								setTimePopover(undefined);
							}
						}}
						styles={{
							dayFontSize: 13,
							circleFontSizeRatio: 2,
							headerFontSize: 13,
							weekDayFontSize: 10,
							arrowSize: 13,
							highlightStyles: {
								today: {
									backgroundColor: "white",
									borderColor: "gray",
								},
								value: {
									backgroundColor: appTheme.colors.COLOR_ACTION_BLUE,
									color: "white",
								},
							},
						}}
					/>
					{timePopover && (
						<div className="time-popover" style={{ left: timePopover.left, top: timePopover.top }}>
							<Popover pointTo="top" styles={{ arrowSize: 10, offset: "10px", width: 115 }}>
								{timePopover.shifts.map((shift) => (
									<AppText key={shift.start + ""} size="s">
										{shiftTimeFromTo(shift)}
									</AppText>
								))}
							</Popover>
						</div>
					)}
				</div>
				{/* data-job: right-clicking the sidebar opens the global job context menu
				    (ContactContextMenu). The customer name below carries its own
				    data-customer marker, which wins when nested. */}
				{job && (
					<div className="job-details-ctn" data-job={job.id}>
						<div className="tag-ctn">
							<div className="tag">
								<Tag
									color="secondary"
									text={job.hidden ? t("job.jobStatus.hidden") : t("job.bookingType.open")}
									textOnHover={t("job.jobStatus.createdBy", {
										name: fullNameOrId(job.metadata.createdBy),
										date: moment(job.metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE_TIME),
									})}
								/>
							</div>

							{job.metadata.createdByRole === USER_TYPE.CUSTOMER_USER && (
								<div className="tag">
									<Tag
										color="actionBlue"
										text={t("job.createdByCustomer")}
										textOnHover={t("job.jobStatus.createdBy", {
											name: fullNameOrId(job.metadata.createdBy),
											date: moment(job.metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE_TIME),
										})}
									/>
								</div>
							)}

							<JobStatusTag job={job} />

							{job.isLongTerm && (
								<div className="tag">
									<Tag color="orange" text={t("job.longTermTag")} />
								</div>
							)}
						</div>

						{/* Descriptive job tags (org jobTagTypes) — chips plus an inline
						    picker when editable. Hidden when the job has no tags and the
						    org defines no job tag types. */}
						<DetailHeaderTags tags={jobTags} options={jobTagOptions} onToggleTag={onToggleTag} />

						{job.status === JOB_STATUS.DECLINED && denialReasonLabel(job.metadata.denialReason) && (
							<InfoBlock
								label="DENIAL_REASON"
								value={denialReasonLabel(job.metadata.denialReason)}
								ignoreTranslation
								className="info-block"
							/>
						)}

						{/* data-customer: right-clicking the customer name opens the global
						    customer context menu (ContactContextMenu). */}
						<div data-customer={job.customer.id}>
							<InfoBlock
								label=""
								value={job.customer.name}
								link={`/customers/${job.customer.id}/profile`}
								linkTarget="_blank"
								className="info-block"
								lineClamp={1}
							/>
						</div>
						{job.internalNotes && (
							<InfoBlock
								label="INTERNAL_NOTES"
								value={addDotsIfLong(job.internalNotes ?? "", 30)}
								className="info-block"
							/>
						)}
						{contactPerson && (
							<InfoBlocStyles className="info-block">
								<Flex direction="column">
									<AppText margin="0 0 0.1rem 0" size="m" weight="bold" color="grayTextDark">
										{t("job.contactPerson")}
									</AppText>
									<AppText margin="0.25rem 0" weight="regular">
										{contactPerson?.firstName || ""}
										{contactPerson?.firstName ? " " : ""}
										{contactPerson?.lastName || ""}
									</AppText>
									{contactPerson.profile && (
										<>
											{contactPerson.profile.title && (
												<AppText margin="0.25rem 0" weight="regular">
													{contactPerson.profile.title}
												</AppText>
											)}
											{contactPerson.profile.phoneNumber && (
												<a href={`tel:${contactPerson.profile.phoneNumber}`}>
													<AppText
														margin="0.25rem 0"
														weight="bold"
														textDecoration="underline"
														color="actionBlue"
													>
														{contactPerson.profile.phoneNumber}
													</AppText>
												</a>
											)}
										</>
									)}
									{contactPerson.email && (
										<a
											href={`mailto:${contactPerson.email}`}
											title={contactPerson.email}
											target="_blank"
										>
											<AppText
												margin="0.25rem 0"
												weight="bold"
												textDecoration="underline"
												color="actionBlue"
											>
												{addDotsIfLong(contactPerson.email, 30)}
											</AppText>
										</a>
									)}
								</Flex>
							</InfoBlocStyles>
						)}
						<InfoBlock
							label="ADDRESS"
							value={address || "-"}
							lineClamp={1}
							href={address ? `https://maps.google.com?q=${address.replace(" ", "")}` : undefined}
							linkTarget="_blank"
							className="info-block"
						/>

						<InfoBlock
							label="DATE"
							value={getFromToWeekdayDate(job.startDate, job.endDate)}
							className="info-block"
						/>
						<InfoBlock
							label="CONTRACT"
							value={job.contract?.name}
							lineClamp={1}
							link={`/contracts/${job.contract.id}/contract`}
							linkTarget="_blank"
							className="info-block"
						/>

						<InfoBlock
							label="POOL"
							value={organisation?.pools?.find((p) => p.key === job.pool)?.name || job.pool}
							className="info-block"
						/>

						<InfoBlock
							label="job.hourlyPrice"
							value={formatCurrencyString(job.price.hourlyPrice)}
							className="info-block"
						/>
						<InfoBlock label="TB &amp; TG" value={tgTb} className="info-block" />

						<InfoBlock
							label="COMPETENCE"
							value={displayCompetences(competences, job.competence)}
							className="info-block"
						/>
						<InfoBlock
							label="BOOKING_TYPE"
							value={t(`BOOKING_TYPE_${job.bookingType}`)}
							className="info-block"
						/>
					</div>
				)}
			</div>
		</StyledJobHeader>
	);
};

export default JobHeader;

function fullNameOrId(user?: Partial<User>) {
	if (!user) {
		return "";
	} else if (user.firstName && user.lastName) {
		const isCustomer = user.roles?.includes("CUSTOMER_USER");
		return user.firstName + " " + user.lastName + (isCustomer ? " (kund)" : "");
	}
	return user.id;
}

const StyledJobHeader = styled.div`
	background-color: ${(props) => props.theme.colors.COLOR_WHITE};
	border-right: 1px solid ${(props) => props.theme.colors.COLOR_LIGHT_GRAY2};
	padding: 1.25rem;

	.date-picker-ctn {
		position: relative;
		width: 100%;
		height: 140px;
		background-color: #f9f9f9;
		border-radius: 20px;
		padding: 1rem 1rem 0.75rem 1rem;
		margin: 1.25rem 0rem;
		border: 1px solid ${(props) => props.theme.colors.COLOR_LIGHT_GRAY2};

		.date-text {
			font-weight: 400;
		}

		.weekdays {
			padding-bottom: 4px;
		}

		.time-popover {
			position: absolute;
			z-index: 2;
		}
	}

	.tag-ctn {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.breadcrumb {
		margin-left: 1.5rem;
	}

	.job-details-ctn {
		display: flex;
		flex-direction: column;
		gap: 1.4rem;
		padding: 0 0.5rem;
	}

	.info-block {
		margin: 0;
	}

	.competence-row {
		margin-left: 1.5rem;

		.info-block {
			margin-top: 0;
		}
	}
`;
